import {
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
} from '@untitledui/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';

import { useMailchimpSyncHealth } from '../hooks/useMailchimpSyncHealth';

interface SyncHealthPanelProps {
  orgId: number | string;
}

const STAT_LABEL_CLASS = 'text-body-small text-[var(--color-text-secondary)]';
const STAT_VALUE_CLASS =
  'text-body-small font-medium text-[var(--color-text-primary)]';
const STAT_VALUE_WARNING_CLASS =
  'text-body-small font-semibold text-[var(--color-text-warning-primary)]';

export function SyncHealthPanel({ orgId }: SyncHealthPanelProps) {
  const { data, isLoading } = useMailchimpSyncHealth(orgId);
  const [errorsExpanded, setErrorsExpanded] = useState(false);

  const handleToggleErrors = useCallback(() => {
    setErrorsExpanded(prev => !prev);
  }, []);

  const outbound = data?.outbound;
  const inbound = data?.inbound;
  const errors = useMemo(() => outbound?.errors ?? [], [outbound]);

  useEffect(() => {
    errors.forEach(err => {
      console.error('sync row error', err);
    });
  }, [errors]);

  const failing = outbound?.failing ?? 0;
  const permanentlyFailed = outbound?.permanently_failed ?? 0;
  // pending is the *total* unprocessed count and includes failing AND
  // permanently-failed rows, so the genuinely-queued (not-yet-errored) count
  // is the difference. Clamp at 0 to stay robust against any transient
  // over/under-count between the queries.
  const queued = Math.max(
    0,
    (outbound?.pending ?? 0) - failing - permanentlyFailed
  );
  const inboundQueued = inbound?.pending ?? 0;
  const skipped = data?.skipped_field_count ?? 0;
  const hasIssues = failing > 0;

  if (isLoading || !data?.connected) {
    return null;
  }

  return (
    <div className='mt-6 border-t border-[var(--color-border-secondary)] pt-4'>
      <div className='mb-3 flex items-center gap-2'>
        {hasIssues ? (
          <AlertTriangle
            className='h-4 w-4 text-utility-warning-600'
            aria-hidden='true'
          />
        ) : (
          <CheckCircle
            className='h-4 w-4 text-utility-success-600'
            aria-hidden='true'
          />
        )}
        <h4 className='text-sm font-semibold text-[var(--color-text-primary)]'>
          Sync health
        </h4>
        {hasIssues ? (
          <Badge type='pill-color' color='warning'>
            Needs attention
          </Badge>
        ) : (
          <Badge type='pill-color' color='success'>
            Healthy
          </Badge>
        )}
      </div>

      <dl className='space-y-1.5'>
        <div className='flex items-center justify-between'>
          <dt className={STAT_LABEL_CLASS}>
            Outbound queued (Ahoy → Mailchimp)
          </dt>
          <dd className={STAT_VALUE_CLASS}>{queued}</dd>
        </div>
        {failing > 0 && (
          <div className='flex items-center justify-between'>
            <dt className={STAT_LABEL_CLASS}>
              Outbound retrying (Ahoy → Mailchimp)
            </dt>
            <dd className={STAT_VALUE_WARNING_CLASS}>{failing}</dd>
          </div>
        )}
        {permanentlyFailed > 0 && (
          <div className='flex items-center justify-between'>
            <dt className={STAT_LABEL_CLASS}>
              Permanently failed (Ahoy → Mailchimp)
            </dt>
            <dd className={STAT_VALUE_CLASS}>{permanentlyFailed}</dd>
          </div>
        )}
        <div className='flex items-center justify-between'>
          <dt className={STAT_LABEL_CLASS}>
            Inbound queued (Mailchimp → Ahoy)
          </dt>
          <dd className={STAT_VALUE_CLASS}>{inboundQueued}</dd>
        </div>
        {skipped > 0 && (
          <div className='flex items-center justify-between'>
            <dt className={STAT_LABEL_CLASS}>Skipped fields</dt>
            <dd className={STAT_VALUE_CLASS}>{skipped}</dd>
          </div>
        )}
      </dl>

      {errors.length > 0 && (
        <div className='mt-3'>
          <Button
            color='link-destructive'
            size='sm'
            onClick={handleToggleErrors}
            aria-expanded={errorsExpanded}
            iconTrailing={errorsExpanded ? ChevronUp : ChevronDown}
          >
            {errorsExpanded ? 'Hide error details' : 'View error details'}
          </Button>

          {errorsExpanded && (
            <>
              {failing > errors.length && (
                <p className='mt-2 text-xs text-[var(--color-text-tertiary)]'>
                  Showing the {errors.length} most-retried of {failing} failing.
                </p>
              )}
              <ul className='mt-2 space-y-2'>
                {errors.map(err => (
                  <li
                    key={err.id}
                    className='rounded-lg border border-utility-error-200 bg-utility-error-50 p-3'
                  >
                    <p className='text-xs text-utility-error-900'>
                      We couldn't sync this record. We'll retry automatically.
                    </p>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}
